// Shared by the Report form and /api/reports, so the reasons a viewer can pick
// and the reasons the server accepts can never drift apart.

export const REPORTS_ENDPOINT = '/api/reports'

export const TARGET_POST = 'post'
export const TARGET_MUSE = 'muse'
export const TARGET_PROFILE = 'profile'
export const TARGET_LIVE = 'live'
export const TARGET_MESSAGE = 'message'

export const TARGET_TYPES = [TARGET_POST, TARGET_MUSE, TARGET_PROFILE, TARGET_LIVE, TARGET_MESSAGE]

export const REPORT_REASONS = [
  { value: 'explicit', label: 'Sexually explicit content' },
  { value: 'minor', label: 'Involves someone under 18' },
  { value: 'non_consensual', label: 'Shared without consent' },
  { value: 'copyright', label: 'Copyright or IP infringement' },
  { value: 'hate_violence', label: 'Hate speech or violence' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'spam_scam', label: 'Spam or scam' },
  { value: 'other', label: 'Something else' }
]

export const MAX_DETAILS_LENGTH = 1000
export const MAX_TARGET_ID_LENGTH = 200
export const MAX_PAGE_PATH_LENGTH = 300

const REASON_VALUES = REPORT_REASONS.map((reason) => reason.value)
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const reasonLabel = (value) =>
  (REPORT_REASONS.find((reason) => reason.value === value) || {}).label || value

const readString = (value) => (typeof value === 'string' ? value.trim() : '')

/**
 * Checks a report submitted by the browser. Everything is re-read from scratch
 * so nothing the client sends beyond these fields ever reaches Firestore.
 *
 * @param {unknown} body
 * @returns {{ ok: true, report: object } | { ok: false, error: string }}
 */
export const validateReport = (body) => {
  const input = body && typeof body === 'object' ? body : {}

  const targetType = readString(input.targetType)
  if (!TARGET_TYPES.includes(targetType)) return { ok: false, error: 'Unknown report type' }

  const targetId = readString(input.targetId)
  if (!targetId || targetId.length > MAX_TARGET_ID_LENGTH) {
    return { ok: false, error: 'Missing what is being reported' }
  }

  const reason = readString(input.reason)
  if (!REASON_VALUES.includes(reason)) return { ok: false, error: 'Pick a reason' }

  const details = readString(input.details)
  if (details.length > MAX_DETAILS_LENGTH) {
    return { ok: false, error: `Keep details under ${MAX_DETAILS_LENGTH} characters` }
  }
  if (reason === 'other' && !details) {
    return { ok: false, error: 'Tell us a little about the problem' }
  }

  // Only a path on this site is kept, never a full URL, so a report can't be
  // used to plant an outside link in the support inbox.
  const pagePath = readString(input.pagePath)
  const safePagePath =
    pagePath.startsWith('/') && !pagePath.startsWith('//') && pagePath.length <= MAX_PAGE_PATH_LENGTH
      ? pagePath
      : ''

  const contactEmail = readString(input.contactEmail).toLowerCase()
  if (contactEmail && !EMAIL_PATTERN.test(contactEmail)) {
    return { ok: false, error: 'Enter a valid email address' }
  }

  return {
    ok: true,
    report: {
      targetType,
      targetId,
      targetLabel: readString(input.targetLabel).slice(0, MAX_TARGET_ID_LENGTH),
      reason,
      details,
      pagePath: safePagePath,
      contactEmail
    }
  }
}
